import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Autocomplete,
  Divider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
// Removed DatePicker import as 'datum' is not directly in filter schema
// import { DatePicker } from '@mui/x-date-pickers/DatePicker'; 
import { useFilters, invalidateFilterCache, deleteFilter, createFilter, updateFilter } from '../Lookup_components/Filter_lookup';
import { useDomainValues } from '../Lookup_components/document_domainvalueslookup';
import debounce from 'lodash/debounce';
import { supabase } from '../../supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import ClearIcon from '@mui/icons-material/Clear'; // Keep if needed for other fields
import { Autocomplete as MuiAutocomplete } from '@mui/material'; // Import standard Autocomplete
import Draggable from 'react-draggable';

// --- Draggable Dialog Component ---
function DraggableDialog(props) {
  const nodeRef = useRef(null);
  return (
    <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}
// --------------------------------

// --- Autocomplete Component (Reused) ---
const AutocompleteField = memo(({ label, value, onChange, options, loading, keyPrefix, getOptionLabel = (option) => option.description || '', isOptionEqualToValue = (option, value) => option?.domain_value_id === value?.domain_value_id, isReadOnly }) => {
  const [open, setOpen] = useState(false);
  // Find the full option object based on the value (which is likely just the ID)
  const currentOption = useMemo(() => options?.find(option => option.domain_value_id === value) || null, [options, value]);

  // Sort options alphabetically, except for unit fields
  const sortedOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    
    // Don't sort if the label contains "eenheid" (case insensitive)
    const isUnitField = label.toLowerCase().includes('eenheid');
    
    if (isUnitField) {
      return options;
    }
    
    return [...options].sort((a, b) => 
      (a.description || '').localeCompare(b.description || '')
    );
  }, [options, label]);

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={sortedOptions}
      getOptionLabel={getOptionLabel}
      value={currentOption}
      onChange={(event, newValue) => {
        onChange(newValue?.domain_value_id || null);
      }}
      disabled={isReadOnly}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          margin="normal"
        />
      )}
      loading={loading}
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={(props, option) => (
        <li {...props} key={`${keyPrefix}-${option.domain_value_id}-${option.description || option.id || option.name}`}>
          {getOptionLabel(option)}
        </li>
      )}
    />
  );
});

// --- FilterForm Component ---
const FilterForm = memo(({
  filter,
  onLocalChange,
  boreholes,
  boreProcesses,
  domainValues,
  domainValuesLoading,
  lengthUnitsData,
  lengthUnitsLoading,
  referentieVlakData,
  referentieVlakLoading,
  debouncedSave,
  isReadOnly
}) => {

  // Function to handle local state update + trigger debounced save
  const handleFieldChange = (field, value) => {
    let valueToSave = value;
    // If changing a unit field, find the full object to store for display purposes
    if (['upperdepth_uom', 'lowerdepth_uom', 'heightTopFiltertube_uom', 'filtertubeLength_uom', 'filtertubeDiameter_uom'].includes(field)) {
      const unitObject = lengthUnitsData?.['Eenheid']?.find(unit => unit.domain_value_id === value);
      valueToSave = unitObject || null; // Store the object or null
    }

    // 1. Update local state immediately using the passed function
    if (onLocalChange) {
        // If filter.id exists, it's an existing filter, update the list
        if (filter.id) {
            onLocalChange(prevFilters =>
              prevFilters.map(f =>
                f.id === filter.id ? { ...f, [field]: valueToSave } : f
              )
            );
        } else {
            // If no filter.id, it's the new filter, update the single object
            onLocalChange(prev => ({ ...prev, [field]: valueToSave }));
        }
    }

    // 2. Trigger debounced save for the database (only for existing filters)
    if (filter.id && debouncedSave) {
      debouncedSave(filter.id, { [field]: value }); // Pass the ID to debouncedSave
    }
  };

  // Find the currently selected borehole object based on the filter's borehole_id
  const selectedBorehole = useMemo(() => 
    boreholes?.find(b => b.id === filter.borehole_id) || null, 
    [boreholes, filter.borehole_id]
  );

  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
      {/* Filter Name */}
      <TextField
          label="Filter Naam"
          value={filter.name || ''}
          onChange={(e) => handleFieldChange('name', e.target.value || null)}
          fullWidth
          margin="normal"
          disabled={isReadOnly}
      />

      {/* Bovendiepte + Eenheid + Conditie */}
      <Typography variant="subtitle2" sx={{ mt: 1 }}>Bovenkant filtertraject</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Diepte"
          type="number"
          value={filter.upperdepth ?? ''}
          onChange={(e) => handleFieldChange('upperdepth', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
          disabled={isReadOnly}
        />
        <Box sx={{ flex: 1 }}>
          <AutocompleteField
            label="Eenheid"
            value={filter.upperdepth_uom?.domain_value_id ?? null}
            onChange={(newValueId) => handleFieldChange('upperdepth_uom', newValueId)}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix={`upperdepth-uom-${filter.id}`}
            isReadOnly={isReadOnly}
          />
        </Box>
      </Box>
      <AutocompleteField
        label="Conditie"
        value={filter.upperdepth_condition}
        onChange={(newValue) => handleFieldChange('upperdepth_condition', newValue)}
        options={referentieVlakData?.['Hoedanigheid'] || []}
        loading={referentieVlakLoading}
        keyPrefix={`upperdepth-cond-${filter.id}`}
        isReadOnly={isReadOnly}
      />

      {/* Onderdiepte + Eenheid + Conditie */}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>Onderkant filtertraject</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Diepte"
          type="number"
          value={filter.lowerdepth === null || filter.lowerdepth === '' ? '0' : filter.lowerdepth}
          onChange={(e) => handleFieldChange('lowerdepth', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
          disabled={isReadOnly}
        />
        <Box sx={{ flex: 1 }}>
          <AutocompleteField
            label="Eenheid"
            value={filter.lowerdepth_uom?.domain_value_id ?? null}
            onChange={(newValueId) => handleFieldChange('lowerdepth_uom', newValueId)}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix={`lowerdepth-uom-${filter.id}`}
            isReadOnly={isReadOnly}
          />
        </Box>
      </Box>
      <AutocompleteField
        label="Conditie"
        value={filter.lowerdepth_condition}
        onChange={(newValue) => handleFieldChange('lowerdepth_condition', newValue)}
        options={referentieVlakData?.['Hoedanigheid'] || []}
        loading={referentieVlakLoading}
        keyPrefix={`lowerdepth-cond-${filter.id}`}
        isReadOnly={isReadOnly}
      />

      <Divider sx={{ my: 2 }} />

      {/* Hoogte Peilbuis */}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>Hoogte Bovenkant Peilbuis</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Hoogte"
          type="number"
          value={filter.heightTopFiltertube_value ?? ''}
          onChange={(e) => handleFieldChange('heightTopFiltertube_value', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
          disabled={isReadOnly}
        />
        <Box sx={{ flex: 1 }}>
          <AutocompleteField
            label="Eenheid"
            value={filter.heightTopFiltertube_uom?.domain_value_id ?? null}
            onChange={(newValueId) => handleFieldChange('heightTopFiltertube_uom', newValueId)}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix={`heightTopFiltertube-uom-${filter.id}`}
            isReadOnly={isReadOnly}
          />
        </Box>
      </Box>

      {/* Lengte Peilbuis */}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>Lengte Peilbuis</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Lengte"
          type="number"
          value={filter.filtertubeLength_value ?? ''}
          onChange={(e) => handleFieldChange('filtertubeLength_value', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
          disabled={isReadOnly}
        />
        <Box sx={{ flex: 1 }}>
          <AutocompleteField
            label="Eenheid"
            value={filter.filtertubeLength_uom?.domain_value_id ?? null}
            onChange={(newValueId) => handleFieldChange('filtertubeLength_uom', newValueId)}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix={`filtertubeLength-uom-${filter.id}`}
            isReadOnly={isReadOnly}
          />
        </Box>
      </Box>

      {/* Diameter Peilbuis */}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>Diameter Peilbuis</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Diameter"
          type="number"
          value={filter.filtertubeDiameter_value ?? ''}
          onChange={(e) => handleFieldChange('filtertubeDiameter_value', e.target.value ? parseFloat(e.target.value) : null)}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
          disabled={isReadOnly}
        />
        <Box sx={{ flex: 1 }}>
          <AutocompleteField
            label="Eenheid"
            value={filter.filtertubeDiameter_uom?.domain_value_id ?? null}
            onChange={(newValueId) => handleFieldChange('filtertubeDiameter_uom', newValueId)}
            options={lengthUnitsData?.['Eenheid'] || []}
            loading={lengthUnitsLoading}
            keyPrefix={`filtertubeDiameter-uom-${filter.id}`}
            isReadOnly={isReadOnly}
          />
        </Box>
      </Box>

      {/* Materiaal Peilbuis */}
      <TextField
          label="Materiaal Peilbuis"
          value={filter.filtertubeMaterial || ''}
          onChange={(e) => handleFieldChange('filtertubeMaterial', e.target.value || null)}
          fullWidth
          margin="normal"
          disabled={isReadOnly}
      />

      <Divider sx={{ my: 2 }} />

      {/* Boring - Use standard MUI Autocomplete */}
      <MuiAutocomplete
        fullWidth
        options={boreholes || []}
        getOptionLabel={(option) => option.name || `ID: ${option.id?.substring(0, 8)}...`}
        value={selectedBorehole} // Pass the full object or null
        onChange={(event, newValue) => {
          handleFieldChange('borehole_id', newValue?.id || null);
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        disabled={isReadOnly}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label="Gekoppelde Boring" 
            margin="normal" 
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {!boreholes ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}> {/* Use borehole id as key */}
            {option.name || `ID: ${option.id?.substring(0, 8)}...`}
          </li>
        )}
        loading={!boreholes}
        loadingText="Boringen laden..."
        noOptionsText="Geen boringen gevonden"
      />

      {/* Layer Type (added) REMOVED */}
      {/* <AutocompleteField
        label="Laag Type"
        value={filter.layer_type}
        onChange={(newValue) => handleFieldChange('layer_type', newValue)}
        options={domainValues['BodemlaagType'] || []}
        loading={domainValuesLoading}
        keyPrefix={`filter-layertype-${filter.id}`}
      /> */}
      {/* MeetObjectSoort (added - assuming domain value) */}
      <AutocompleteField
        label="Meetobject Type"
        value={filter.MeetObjectSoort}
        onChange={(newValue) => handleFieldChange('MeetObjectSoort', newValue)}
        options={domainValues['MeetObjectSoort'] || []}
        loading={domainValuesLoading}
        keyPrefix={`filter-meetobj-${filter.id}`}
        isReadOnly={isReadOnly}
      />

      {/* Remark */}
      <TextField
        label="Opmerking"
        value={filter.remark || ''}
        onChange={(e) => handleFieldChange('remark', e.target.value || null)}
        fullWidth
        multiline
        rows={3}
        margin="normal"
      />

    </Box>
  );
});
// --- End FilterForm Component ---

export default function FilterTab({ document, isReadOnly }) {
  const [expandedItem, setExpandedItem] = useState(null);
  const [error, setError] = useState(null);
  const [boreholes, setBoreholes] = useState([]);
  const [groupedByBorehole, setGroupedByBorehole] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [newFilter, setNewFilter] = useState({
    remark: null,
    upperdepth: null,
    upperdepth_uom: null,
    upperdepth_condition: null,
    lowerdepth: null,
    lowerdepth_uom: null,
    lowerdepth_condition: null,
    borehole_id: null,
    document_id: document.id,
    MeetObjectSoort: null, // Corrected: Use the actual column name
    name: null,
    heightTopFiltertube_value: null,
    heightTopFiltertube_uom: null,
    filtertubeLength_value: null,
    filtertubeLength_uom: null,
    filtertubeDiameter_value: null,
    filtertubeDiameter_uom: null,
    filtertubeMaterial: null,
  });

  // Memoize the params object for useFilters
  const filterParams = useMemo(() => ({ document_id: document.id }), [document.id]);

  // Fetch filters using the hook with memoized params
  const { filters, loading: filtersLoading, error: filtersError, refetch: refetchFilters } = useFilters(filterParams);
  // Add local state for editable filters
  const [editableFilters, setEditableFilters] = useState([]);

  // Sync filters from hook to local editable state, normalizing foreign keys
  useEffect(() => {
    if (filters) {
      const mappedFilters = filters.map(f => ({
        ...f,
        // Keep the full objects for units, remove normalization
        // upperdepth_uom: f.upperdepth_uom?.domain_value_id ?? f.upperdepth_uom ?? null,
        upperdepth_condition: f.upperdepth_condition?.domain_value_id ?? f.upperdepth_condition ?? null,
        // lowerdepth_uom: f.lowerdepth_uom?.domain_value_id ?? f.lowerdepth_uom ?? null,
        lowerdepth_condition: f.lowerdepth_condition?.domain_value_id ?? f.lowerdepth_condition ?? null,
        // layer_type: f.layer_type?.domain_value_id ?? f.layer_type ?? null, // REMOVED
        // boreprocess ID is already just an ID
        samplingdevice: f.samplingdevice?.domain_value_id ?? f.samplingdevice ?? null,
        measurementObjectType: f.measurementObjectType?.domain_value_id ?? f.measurementObjectType ?? null,
        MeetObjectSoort: f.MeetObjectSoort?.domain_value_id ?? f.MeetObjectSoort ?? null, // Corrected: Use actual column name and extract ID
        // Keep the full objects for new uom fields
        // heightTopFiltertube_uom: f.heightTopFiltertube_uom?.domain_value_id ?? f.heightTopFiltertube_uom ?? null,
        // filtertubeLength_uom: f.filtertubeLength_uom?.domain_value_id ?? f.filtertubeLength_uom ?? null,
        // filtertubeDiameter_uom: f.filtertubeDiameter_uom?.domain_value_id ?? f.filtertubeDiameter_uom ?? null,
      }));

      if (JSON.stringify(mappedFilters) !== JSON.stringify(editableFilters)) {
        setEditableFilters(mappedFilters || []);
      }
    }
  }, [filters]); // Keep dependency only on filters

  // Get necessary domain values
  const domainValueTechnicalNames = useMemo(() => [
    'Eenheid',
    'Hoedanigheid',
    'BodemlaagType',       // For layer_type
    'MonsternameApparaat', // For samplingdevice
    'MeetObjectSoort'      // For MeetObjectSoort
  ], []);
  const { domainValues, loading: domainValuesLoading, error: domainValuesError } = useDomainValues(domainValueTechnicalNames);

  // Fetch specific units for length
  const { domainValues: lengthUnitsData, loading: lengthUnitsLoading, error: lengthUnitsError } = useDomainValues({ technical_name: 'Eenheid', unit_dimension: 'l' });

  // Add new hook call for ReferentieVlak
  const {
    domainValues: referentieVlakData,
    loading: referentieVlakLoading,
    // error: referentieVlakError // Optional: handle error
  } = useDomainValues({ technical_name: 'Hoedanigheid', group_name: 'ReferentieVlak', sortByFrequency: true });

  // --- NEW: Find default IDs --- 
  const meterUnitId = useMemo(() => {
    const units = lengthUnitsData?.['Eenheid'];
    if (!units) return null;
    return units.find(unit => unit.description?.toLowerCase() === 'meter')?.domain_value_id || null;
  }, [lengthUnitsData]);

  const defaultConditionId = useMemo(() => {
    const conditions = referentieVlakData?.['Hoedanigheid'];
    if (!conditions) return null;
    return conditions.find(cond => cond.description?.toLowerCase() === 't.o.v. maaiveld')?.domain_value_id || null;
  }, [referentieVlakData]);

  // Fetch related boreholes and bore processes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: projectData } = await supabase
          .from('document_project')
          .select('id')
          .eq('document_id', document.id)
          .single();

        if (projectData) {
          const projectId = projectData.id;
          // Fetch boreholes
          const { data } = await supabase
            .from('document_borehole')
            .select('id, name')
            .eq('project_id', projectId);
          setBoreholes(data || []);

        }
      } catch (error) {
        console.error('Error fetching related data:', error);
        setError('Fout bij ophalen boringen/boorprocessen.');
      }
    };

    fetchData();
  }, [document.id]);

   // Group filters by borehole ID
  useEffect(() => {
    if (!filtersLoading && editableFilters.length > 0) {
      const grouped = {};
      editableFilters.forEach(filter => {
        const boreholeId = filter.borehole_id || 'no_borehole';
        if (!grouped[boreholeId]) {
          grouped[boreholeId] = [];
        }
        grouped[boreholeId].push(filter);
      });

      // Sort filters within each group using natural sort
      Object.keys(grouped).forEach(boreholeId => {
        grouped[boreholeId].sort((a, b) => {
          // Zet null/undefined namen onderaan
          if (!a.name) return 1;
          if (!b.name) return -1;
          
          // Natuurlijke sortering voor strings met nummers
          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        });
      });

      setGroupedByBorehole(grouped);
    } else {
      setGroupedByBorehole({}); // Clear grouping if loading or no filters
    }
  }, [editableFilters, filtersLoading]);

  // --- UPDATED: Effect to process filters and set defaults --- 
  useEffect(() => {
    // Wait for filters, IDs, and related data to be loaded
    if (filters && !filtersLoading && meterUnitId !== undefined && defaultConditionId !== undefined && boreholes) {
      const processFilters = async () => {
        const updatePromises = [];
        const mappedFilters = filters.map(f => {
          let updates = {};
          let needsUpdate = false;
          let updatedFilter = { ...f }; // Start with a copy

          // Normalize foreign keys (keep this part)
          updatedFilter = {
            ...updatedFilter,
            upperdepth_condition: f.upperdepth_condition?.domain_value_id ?? f.upperdepth_condition ?? null,
            lowerdepth_condition: f.lowerdepth_condition?.domain_value_id ?? f.lowerdepth_condition ?? null,
            samplingdevice: f.samplingdevice?.domain_value_id ?? f.samplingdevice ?? null,
            measurementObjectType: f.measurementObjectType?.domain_value_id ?? f.measurementObjectType ?? null,
            MeetObjectSoort: f.MeetObjectSoort?.domain_value_id ?? f.MeetObjectSoort ?? null,
             // Keep full unit objects in local state for display, but check for defaults based on ID absence
            upperdepth_uom: f.upperdepth_uom,
            lowerdepth_uom: f.lowerdepth_uom,
            heightTopFiltertube_uom: f.heightTopFiltertube_uom,
            filtertubeLength_uom: f.filtertubeLength_uom,
            filtertubeDiameter_uom: f.filtertubeDiameter_uom,
          };

          // --- Apply Default Values Logic --- 
          if (meterUnitId) {
            if (f.upperdepth_uom == null) { 
              updates.upperdepth_uom = meterUnitId; 
              updatedFilter.upperdepth_uom = lengthUnitsData?.['Eenheid']?.find(u => u.domain_value_id === meterUnitId) || null; // Update local state with object
              needsUpdate = true; 
            }
            if (f.lowerdepth_uom == null) { 
              updates.lowerdepth_uom = meterUnitId; 
              updatedFilter.lowerdepth_uom = lengthUnitsData?.['Eenheid']?.find(u => u.domain_value_id === meterUnitId) || null; 
              needsUpdate = true; 
            }
            if (f.heightTopFiltertube_uom == null) { 
              updates.heightTopFiltertube_uom = meterUnitId; 
              updatedFilter.heightTopFiltertube_uom = lengthUnitsData?.['Eenheid']?.find(u => u.domain_value_id === meterUnitId) || null; 
              needsUpdate = true; 
            }
            if (f.filtertubeLength_uom == null) { 
              updates.filtertubeLength_uom = meterUnitId; 
              updatedFilter.filtertubeLength_uom = lengthUnitsData?.['Eenheid']?.find(u => u.domain_value_id === meterUnitId) || null; 
              needsUpdate = true; 
            }
            if (f.filtertubeDiameter_uom == null) { 
              updates.filtertubeDiameter_uom = meterUnitId; 
              updatedFilter.filtertubeDiameter_uom = lengthUnitsData?.['Eenheid']?.find(u => u.domain_value_id === meterUnitId) || null; 
              needsUpdate = true; 
            }
          }

          if (defaultConditionId) {
            if (updatedFilter.upperdepth_condition == null) { 
              updates.upperdepth_condition = defaultConditionId; 
              updatedFilter.upperdepth_condition = defaultConditionId; 
              needsUpdate = true; 
            }
            if (updatedFilter.lowerdepth_condition == null) { 
              updates.lowerdepth_condition = defaultConditionId; 
              updatedFilter.lowerdepth_condition = defaultConditionId; 
              needsUpdate = true; 
            }
          }
          // ----------------------------------

          if (needsUpdate) {
            updatePromises.push(updateFilter(f.id, updates));
          }
          return updatedFilter; // Return the potentially updated filter for local state
        });

        // Update local state immediately if changes occurred
        if (JSON.stringify(mappedFilters) !== JSON.stringify(editableFilters)) {
          setEditableFilters(mappedFilters || []);
        }

        // Wait for all background updates
        if (updatePromises.length > 0) {
          console.log(`Attempting to set default values for ${updatePromises.length} filter fields...`);
          const results = await Promise.allSettled(updatePromises);
          const updateErrors = results.filter(r => r.status === 'rejected').length;
          if (updateErrors > 0) {
            setError(prev => {
              const errorMsg = `Kon voor ${updateErrors} filter(s) de standaard waarde (eenheid/conditie) niet opslaan.`;
              return prev ? `${prev}\n${errorMsg}` : errorMsg;
            });
          }
        }
      };
      processFilters();
    }
  }, [filters, filtersLoading, meterUnitId, defaultConditionId, boreholes, lengthUnitsData]); // Removed editableFilters from dependencies

  // Group filters by borehole ID
  useEffect(() => {
    if (!filtersLoading && editableFilters.length > 0) {
      const grouped = {};
      editableFilters.forEach(filter => {
        const boreholeId = filter.borehole_id || 'no_borehole';
        if (!grouped[boreholeId]) {
          grouped[boreholeId] = [];
        }
        grouped[boreholeId].push(filter);
      });

      // Sort filters within each group using natural sort
      Object.keys(grouped).forEach(boreholeId => {
        grouped[boreholeId].sort((a, b) => {
          // Zet null/undefined namen onderaan
          if (!a.name) return 1;
          if (!b.name) return -1;
          
          // Natuurlijke sortering voor strings met nummers
          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        });
      });

      setGroupedByBorehole(grouped);
    } else {
      setGroupedByBorehole({}); // Clear grouping if loading or no filters
    }
  }, [editableFilters, filtersLoading]);

  // Debounced save function for individual filter updates
  const debouncedSave = useCallback(
    debounce(async (id, updates) => {
      try {
        // Only perform the database update
        const { data: updatedData, error } = await updateFilter(id, updates);

        if (error) {
          // Handle potential errors (e.g., show an error message, rollback optimistic update if needed)
          console.error('Save error:', error);
          setError(`Fout bij opslaan filter ${id.substring(0, 4)}...: ${error.message}`);
          // Optional: Rollback the optimistic update if DB save fails
          // This would involve refetching or reverting the specific change in editableFilters
          // For simplicity, we'll rely on the next refetch or user action to correct discrepancy for now
        } else {
           setError(null); // Clear error on successful save
           // No need to call setEditableFilters here anymore
        }

        // Cache invalidation is handled within updateFilter
      } catch (error) {
        // Catch any unexpected errors during the async operation
        console.error('Unexpected save error:', error);
        setError(`Onverwachte fout bij opslaan filter ${id.substring(0, 4)}...`);
      }
    }, 1000), // 1 second debounce delay
    [document.id] 
  );

  // Handle creation of a new filter
  const handleCreateFilter = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Basic validation (optional)
      // if (!newFilter.borehole_id) {
      //    throw new Error("Gekoppelde boring is verplicht.");
      // }

      const createData = {
        ...newFilter,
        id: uuidv4(),
        document_id: document.id, // Ensure document_id is set
        // --- NEW: Apply defaults on create --- 
        upperdepth_uom: newFilter.upperdepth_uom || meterUnitId,
        lowerdepth_uom: newFilter.lowerdepth_uom || meterUnitId,
        heightTopFiltertube_uom: newFilter.heightTopFiltertube_uom || meterUnitId,
        filtertubeLength_uom: newFilter.filtertubeLength_uom || meterUnitId,
        filtertubeDiameter_uom: newFilter.filtertubeDiameter_uom || meterUnitId,
        upperdepth_condition: newFilter.upperdepth_condition || defaultConditionId,
        lowerdepth_condition: newFilter.lowerdepth_condition || defaultConditionId,
        // -------------------------------------
      };

      const data = await createFilter(createData);
      if (!data) throw new Error("Aanmaken mislukt, geen data terug ontvangen.");

      // Add the new filter to the local state immediately
      // Important: Normalize foreign keys if needed before adding
      const normalizedNewFilter = {
          ...data,
          // Add normalization here if createFilter doesn't return populated fields
      };
      setEditableFilters(prev => [...prev, normalizedNewFilter]);

      setIsCreating(false);
      setNewFilter({ // Reset form state
        remark: null, upperdepth: null, upperdepth_uom: null, upperdepth_condition: null,
        lowerdepth: null, lowerdepth_uom: null, lowerdepth_condition: null,
        borehole_id: null, document_id: document.id, 
        MeetObjectSoort: null, // Corrected: Use the actual column name
        name: null,
        heightTopFiltertube_value: null,
        heightTopFiltertube_uom: null,
        filtertubeLength_value: null,
        filtertubeLength_uom: null,
        filtertubeDiameter_value: null,
        filtertubeDiameter_uom: null,
        filtertubeMaterial: null,
      });
      // Invalidation is handled by createFilter
      refetchFilters(); // <-- EXPLICITLY REFETCH

    } catch (error) {
      console.error('Error creating filter:', error);
      setError(`Fout bij aanmaken filter: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deletion of a filter
  const handleDeleteFilter = async (filterId) => {
    try {
      setIsDeleting(true);
      setError(null);
      await deleteFilter(filterId);

      // Remove the deleted filter from local state
      setEditableFilters(prev => prev.filter(f => f.id !== filterId));

      setIsDeleteDialogOpen(false);
      setSelectedFilterId(null);
      // Cache invalidation handled by deleteFilter
      refetchFilters(); // <-- EXPLICITLY REFETCH

    } catch (error) {
      console.error('Error deleting filter:', error);
      setError(`Fout bij verwijderen filter: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Combine loading states
  const combinedLoading = filtersLoading || domainValuesLoading || lengthUnitsLoading || !boreholes || referentieVlakLoading; // Added referentieVlakLoading

  if (combinedLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check for errors after loading
  const combinedError = filtersError || domainValuesError || lengthUnitsError || error;
  if (combinedError) {
    return <Alert severity="error">Fout bij laden gegevens: {String(combinedError)}</Alert>;
  }

  // Check if no filters exist
  const hasFilters = editableFilters.length > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreating(true)}
          sx={{ bgcolor: 'primary.main', color: 'white' }}
          disabled={isReadOnly}
        >
          Nieuw Filter
        </Button>
      </Box>

      {/* --- Create Dialog --- */}
      <Dialog 
        open={isCreating} 
        onClose={() => setIsCreating(false)} 
        maxWidth="sm"
        PaperComponent={DraggableDialog}
        aria-labelledby="draggable-dialog-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
        PaperProps={{
          sx: { 
            minWidth: '500px',
          }
        }}
      >
        <DialogTitle 
          style={{ cursor: 'move' }} 
          id="draggable-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <DragIndicatorIcon />
          Nieuw Filter
        </DialogTitle>
        <DialogContent>
            <FilterForm
                filter={newFilter}
                onLocalChange={setNewFilter}
                boreholes={boreholes}
                domainValues={domainValues}
                domainValuesLoading={domainValuesLoading}
                lengthUnitsData={lengthUnitsData}
                lengthUnitsLoading={lengthUnitsLoading}
                referentieVlakData={referentieVlakData}
                referentieVlakLoading={referentieVlakLoading}
                debouncedSave={null}
                isReadOnly={isReadOnly}
            />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreating(false)} disabled={isReadOnly}>Annuleren</Button>
          <Button
            onClick={handleCreateFilter}
            variant="contained"
            disabled={isSubmitting || isReadOnly}
            startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
          >
            {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Delete Dialog --- */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Filter verwijderen</DialogTitle>
        <DialogContent>
          <Typography>
            Weet u zeker dat u dit filter wilt verwijderen?
            Dit kan niet ongedaan worden gemaakt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} disabled={isReadOnly}>
            Annuleren
          </Button>
          <Button
            onClick={() => handleDeleteFilter(selectedFilterId)}
            color="error"
            disabled={isDeleting || isReadOnly}
            startIcon={isDeleting ? <CircularProgress size={24} /> : null}
          >
            {isDeleting ? 'Bezig met verwijderen...' : 'Verwijderen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Filter List --- */}
      {!hasFilters ? (
        <Typography variant="body1" sx={{ mt: 2 }}>Geen filters gevonden voor dit document.</Typography>
      ) : (
        <List>
          {Object.entries(groupedByBorehole).map(([boreholeId, filtersInGroup]) => {
            const borehole = boreholes.find(b => b.id === boreholeId);
            const groupTitle = boreholeId === 'no_borehole'
              ? 'Filters zonder gekoppelde boring'
              : borehole?.name || `Boring ID: ${boreholeId.substring(0, 8)}...`;

            return (
              <Box key={boreholeId} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ bgcolor: 'primary.main', color: 'white', p: 1, borderRadius: 1, mb: 1 }}
                >
                  {groupTitle}
                </Typography>
                {filtersInGroup.map((filter) => {
                  // filter here is from editableFilters, which now contains full unit objects
                  
                  // Create a display name for the list item using the updated filter object
                  const upper = filter.upperdepth !== null ? 
                    `${filter.upperdepth} ${filter.upperdepth_uom?.description || ''}`.trim() : '?';
                  const lower = filter.lowerdepth !== null ? 
                    `${filter.lowerdepth} ${filter.lowerdepth_uom?.description || ''}`.trim() : '?';
                  const displayName = `${filter.name || '(naamloos)'}: ${upper} - ${lower}`.trim();

                  return (
                    <React.Fragment key={filter.id}>
                      <ListItem
                        divider
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <ListItemText
                          primary={displayName}
                          onClick={() => setExpandedItem(expandedItem === filter.id ? null : filter.id)}
                          sx={{ cursor: 'pointer', flexGrow: 1, mr: 2 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            onClick={() => setExpandedItem(expandedItem === filter.id ? null : filter.id)}
                          >
                            {expandedItem === filter.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => {
                              setSelectedFilterId(filter.id);
                              setIsDeleteDialogOpen(true);
                            }}
                            disabled={isReadOnly}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </ListItem>
                      <Collapse in={expandedItem === filter.id} timeout="auto" unmountOnExit>
                        <FilterForm
                          filter={editableFilters.find(f => f.id === filter.id) || filter}
                          onLocalChange={setEditableFilters}
                          boreholes={boreholes}
                          domainValues={domainValues}
                          domainValuesLoading={domainValuesLoading}
                          lengthUnitsData={lengthUnitsData}
                          lengthUnitsLoading={lengthUnitsLoading}
                          referentieVlakData={referentieVlakData}
                          referentieVlakLoading={referentieVlakLoading}
                          debouncedSave={debouncedSave}
                          isReadOnly={isReadOnly}
                        />
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </Box>
            );
          })}
        </List>
      )}
    </Box>
  );
}
